<template>
	<div class="preset-text-input" v-bind="$attrs">
		<label v-if="label" class="preset-text-input__label" :for="inputId">{{ label }}</label>
		<div
			ref="shellRef"
			class="preset-text-input__shell"
			:class="{ 'is-open': dropdownOpen, 'is-disabled': disabled, 'is-multiline': multiline }"
		>
			<textarea
				v-if="multiline"
				ref="inputRef"
				:id="inputId"
				class="preset-text-input__field preset-text-input__field--textarea"
				role="combobox"
				autocomplete="off"
				:disabled="disabled"
				:value="modelValue"
				:placeholder="placeholder"
				:maxlength="maxLength"
				:rows="rows"
				:aria-label="inputAriaLabel || undefined"
				:aria-expanded="dropdownOpen"
				:aria-controls="listboxId"
				aria-autocomplete="list"
				@input="handleInput"
				@focus="handleFocus"
				@blur="handleBlur"
				@keydown.esc.prevent="closeDropdown"
			></textarea>
			<input
				v-else
				ref="inputRef"
				:id="inputId"
				class="preset-text-input__field"
				type="text"
				role="combobox"
				autocomplete="off"
				:disabled="disabled"
				:value="modelValue"
				:placeholder="placeholder"
				:maxlength="maxLength"
				:aria-label="inputAriaLabel || undefined"
				:aria-expanded="dropdownOpen"
				:aria-controls="listboxId"
				:aria-activedescendant="activeOptionId || undefined"
				aria-autocomplete="list"
				@input="handleInput"
				@focus="handleFocus"
				@blur="handleBlur"
				@keydown.down.prevent="navigateOptions(1)"
				@keydown.up.prevent="navigateOptions(-1)"
				@keydown.enter.prevent="selectHighlightedOption"
				@keydown.esc.prevent="closeDropdown"
			/>
			<button
				v-if="showManageButton"
				ref="manageButtonRef"
				type="button"
				class="preset-text-input__manage"
				:disabled="disabled"
				:aria-label="manageButtonLabel"
				:title="manageButtonLabel"
				@pointerdown.prevent
				@click="openManager"
			>
				<svg viewBox="0 0 24 24" aria-hidden="true">
					<path d="M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M6 14v6" />
				</svg>
			</button>

			<div
				v-if="dropdownOpen"
				class="preset-text-input__dropdown"
				:class="{ 'is-above': dropdownPlacement === 'above' }"
			>
				<div v-if="filteredOptions.length" :id="listboxId" role="listbox">
					<button
						v-for="(option, index) in filteredOptions"
						:id="optionId(index)"
						:key="option"
						type="button"
						class="preset-text-input__option"
						:class="{
							'is-highlighted': highlightedIndex === index,
							'is-selected': option === modelValue,
						}"
						role="option"
						:aria-selected="option === modelValue"
						@mouseenter="highlightedIndex = index"
						@pointerdown.prevent="selectOption(option)"
					>
						<span>{{ option }}</span>
						<span v-if="option === modelValue" class="preset-text-input__check" aria-hidden="true">✓</span>
					</button>
				</div>
				<div v-else :id="listboxId" class="preset-text-input__empty" role="status">
					{{ normalizedOptions.length ? 'No matching values' : 'No saved values yet' }}
				</div>
			</div>
		</div>
	</div>

	<PresetValuesManager
		ref="managerRef"
		:options="managerOptions || options"
		:title="manageTitle"
		:description="manageDescription"
		:close-label="managerCloseLabel"
		:option-label="optionLabel"
		:remove-option-label="removeOptionLabel"
		:save-options="saveOptions"
		:save-error-message="saveErrorMessage"
		:max-options="maxOptions"
		:max-length="maxLength"
		@open="handleManagerOpen"
		@close="managerOpen = false"
		@saved="handleOptionsSaved"
	/>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
import PresetValuesManager from '@/components/documents/PresetValuesManager.vue';
import {
	MAX_ASSIGNMENT_SUBTITLE_LENGTH,
	MAX_ASSIGNMENT_SUBTITLE_OPTIONS,
} from '@/utils/assignmentSubtitlePresets';

defineOptions({ inheritAttrs: false });

const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	options: {
		type: Array,
		default: () => [],
	},
	label: {
		type: String,
		default: 'Subtitle',
	},
	placeholder: {
		type: String,
		default: 'Enter or select a subtitle',
	},
	manageTitle: {
		type: String,
		default: 'Manage subtitle options',
	},
	manageDescription: {
		type: String,
		default: 'These values are available in every Assignment Confirmation for this project.',
	},
	manageButtonLabel: {
		type: String,
		default: 'Manage subtitle options',
	},
	managerCloseLabel: {
		type: String,
		default: 'Close subtitle options',
	},
	optionLabel: {
		type: String,
		default: 'Subtitle option',
	},
	removeOptionLabel: {
		type: String,
		default: 'Remove subtitle option',
	},
	inputAriaLabel: {
		type: String,
		default: '',
	},
	managerOptions: {
		type: Array,
		default: null,
	},
	showManageButton: {
		type: Boolean,
		default: true,
	},
	saveErrorMessage: {
		type: String,
		default: 'Could not save subtitle values. Please try again.',
	},
	saveOptions: {
		type: Function,
		default: null,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	multiline: {
		type: Boolean,
		default: false,
	},
	rows: {
		type: Number,
		default: 3,
	},
	maxOptions: {
		type: Number,
		default: MAX_ASSIGNMENT_SUBTITLE_OPTIONS,
	},
	maxLength: {
		type: Number,
		default: MAX_ASSIGNMENT_SUBTITLE_LENGTH,
	},
});

const emit = defineEmits(['update:modelValue', 'change', 'select', 'manage-open', 'options-saved']);

const instanceId = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2);
const inputId = `preset-text-input-${instanceId}`;
const listboxId = `${inputId}-listbox`;

const shellRef = ref(null);
const inputRef = ref(null);
const manageButtonRef = ref(null);
const managerRef = ref(null);
const dropdownOpen = ref(false);
const dropdownPlacement = ref('below');
const query = ref('');
const highlightedIndex = ref(-1);
const managerOpen = ref(false);

const normalizedOptions = computed(() => {
	const normalized = [];
	const seen = new Set();
	for (const value of Array.isArray(props.options) ? props.options : []) {
		if (normalized.length >= props.maxOptions) break;
		const option = String(value ?? '')
			.trim()
			.slice(0, props.maxLength);
		const key = option.toLocaleLowerCase();
		if (!option || seen.has(key)) continue;
		seen.add(key);
		normalized.push(option);
	}
	return normalized;
});
const filteredOptions = computed(() => {
	const search = query.value.trim().toLocaleLowerCase();
	if (!search) return normalizedOptions.value;
	return normalizedOptions.value.filter((option) => option.toLocaleLowerCase().includes(search));
});
const activeOptionId = computed(() =>
	dropdownOpen.value && highlightedIndex.value >= 0
		? optionId(highlightedIndex.value)
		: '',
);

const optionId = (index) => `${listboxId}-option-${index}`;

const positionDropdown = () => {
	const rect = shellRef.value?.getBoundingClientRect?.();
	if (!rect || typeof window === 'undefined') return;
	const desiredHeight = Math.min(240, Math.max(48, normalizedOptions.value.length * 40));
	const spaceBelow = window.innerHeight - rect.bottom;
	const spaceAbove = rect.top;
	dropdownPlacement.value = spaceBelow < desiredHeight && spaceAbove > spaceBelow ? 'above' : 'below';
};

const openDropdown = () => {
	if (props.disabled || managerOpen.value) return;
	dropdownOpen.value = true;
	highlightedIndex.value = -1;
	nextTick(positionDropdown);
};

const closeDropdown = () => {
	dropdownOpen.value = false;
	highlightedIndex.value = -1;
	query.value = '';
};

const handleFocus = () => {
	query.value = '';
	openDropdown();
};

const handleInput = (event) => {
	const value = event.target.value;
	query.value = value;
	emit('update:modelValue', value);
	emit('change', value);
	openDropdown();
	resizeTextarea();
};

const handleBlur = () => {
	nextTick(() => {
		if (!shellRef.value?.contains(document.activeElement)) closeDropdown();
	});
};

const selectOption = (option) => {
	emit('update:modelValue', option);
	emit('change', option);
	emit('select', option);
	closeDropdown();
	inputRef.value?.blur?.();
};

const navigateOptions = (direction) => {
	if (!dropdownOpen.value) openDropdown();
	if (!filteredOptions.value.length) return;
	const current = highlightedIndex.value;
	highlightedIndex.value =
		current < 0
			? direction > 0 ? 0 : filteredOptions.value.length - 1
			: (current + direction + filteredOptions.value.length) % filteredOptions.value.length;
	nextTick(() => {
		document.getElementById(optionId(highlightedIndex.value))?.scrollIntoView?.({ block: 'nearest' });
	});
};

const selectHighlightedOption = () => {
	if (
		dropdownOpen.value &&
		highlightedIndex.value >= 0 &&
		highlightedIndex.value < filteredOptions.value.length
	) {
		selectOption(filteredOptions.value[highlightedIndex.value]);
		return;
	}
	closeDropdown();
};

const handleManagerOpen = () => {
	managerOpen.value = true;
	emit('manage-open');
};

const handleOptionsSaved = (values) => emit('options-saved', values);

const openManager = (trigger = {}) => {
	if (props.disabled) return;
	closeDropdown();
	const returnFocus = trigger?.returnFocus || trigger?.currentTarget || manageButtonRef.value;
	managerRef.value?.open?.({ returnFocus });
};

const focus = () => inputRef.value?.focus?.({ preventScroll: true });

const resizeTextarea = () => {
	if (!props.multiline || !inputRef.value) return;
	inputRef.value.style.height = 'auto';
	inputRef.value.style.height = `${inputRef.value.scrollHeight}px`;
};

watch(
	() => props.modelValue,
	() => nextTick(resizeTextarea),
	{ immediate: true },
);

defineExpose({ openManager, focus });
</script>

<style scoped>
.preset-text-input {
	display: flex;
	flex-direction: column;
	gap: 4px;
	width: 100%;
	min-width: 0;
}

.preset-text-input__label {
	color: var(--text-color, #333);
	font-size: 0.9rem;
	font-weight: 500;
}

.preset-text-input__shell {
	position: relative;
	display: flex;
	align-items: stretch;
	width: 100%;
	height: var(--proposal-control-height, 38px);
	min-height: var(--proposal-control-height, 38px);
	border: var(--proposal-control-border, 1px solid var(--border-color, #d1d5db));
	border-radius: var(--proposal-control-radius, 8px);
	background: var(--proposal-control-background, var(--noc-background, #fff));
	box-shadow: var(--proposal-control-shadow, var(--box-shadow));
	box-sizing: border-box;
	transition: border-color 120ms ease, box-shadow 120ms ease;
}

.preset-text-input__shell:hover {
	border-color: var(--border-color-active, #9ca3af);
}

.preset-text-input__shell:focus-within,
.preset-text-input__shell.is-open {
	border-color: var(--noc-blue, #1261c9);
}

.preset-text-input__shell.is-disabled {
	opacity: 0.65;
}

.preset-text-input__shell.is-multiline {
	height: auto;
	min-height: 76px;
}

.preset-text-input__field {
	flex: 1 1 auto;
	min-width: 0;
	height: 100%;
	padding: 6px 12px;
	border: 0;
	border-radius: inherit;
	outline: none;
	background: transparent;
	color: var(--text-color, #333);
	font: inherit;
	font-size: var(--document-input-font-size, 0.85rem);
	line-height: 1;
	box-sizing: border-box;
}

.preset-text-input__field:focus,
.preset-text-input__field:focus-visible {
	border: 0 !important;
	outline: none;
	box-shadow: none;
}

.preset-text-input__field--textarea {
	height: auto;
	min-height: 74px;
	padding: 9px 12px;
	line-height: 1.35;
	resize: none;
	overflow: hidden;
}

.preset-text-input__manage {
	display: inline-flex;
	flex: 0 0 38px;
	align-items: center;
	justify-content: center;
	width: 38px;
	padding: 0;
	border: 0;
	border-left: 1px solid var(--border-color, #d1d5db);
	border-radius: 0 calc(var(--proposal-control-radius, 8px) - 1px) calc(var(--proposal-control-radius, 8px) - 1px) 0;
	background: transparent;
	color: var(--text-color-secondary, #6b7280);
	cursor: pointer;
}

.preset-text-input__manage svg {
	width: 18px;
	height: 18px;
	fill: none;
	stroke: currentColor;
	stroke-linecap: round;
	stroke-linejoin: round;
	stroke-width: 1.8;
}

.preset-text-input__manage:hover,
.preset-text-input__manage:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	color: var(--primary-color, #1261c9);
	outline: none;
}

.preset-text-input__manage:disabled {
	cursor: default;
}

.preset-text-input__dropdown {
	position: absolute;
	z-index: 130;
	top: calc(100% + 5px);
	left: -1px;
	right: -1px;
	max-height: 240px;
	overflow-y: auto;
	padding: 4px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-shadow: 0 12px 30px rgba(15, 23, 42, 0.16);
	box-sizing: border-box;
}

.preset-text-input__dropdown.is-above {
	top: auto;
	bottom: calc(100% + 5px);
}

.preset-text-input__option {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	min-height: 38px;
	padding: 8px 10px;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.85rem;
	line-height: 1.3;
	text-align: left;
	cursor: pointer;
}

.preset-text-input__option:hover,
.preset-text-input__option.is-highlighted {
	background: var(--noc-background-secondary, #f5f6f8);
}

.preset-text-input__option.is-selected {
	color: var(--primary-color, #1261c9);
	font-weight: 600;
}

.preset-text-input__check {
	flex: 0 0 auto;
}

.preset-text-input__empty {
	padding: 11px 10px;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.83rem;
}
</style>
